import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface EmailAttachment {
  filename: string;
  content: string; // Base64
  content_type: string;
}

interface SendEmailParams {
  leadId: string;
  to: string[];
  cc?: string[];
  bcc?: string[];
  subject: string;
  bodyText?: string;
  bodyHtml?: string;
  replyToEmailId?: string;
  attachments?: EmailAttachment[];
}

export const useSendLeadEmail = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (params: SendEmailParams) => {
      const { data, error } = await supabase.functions.invoke("send-lead-email", {
        body: params,
      });

      if (error) {
        throw new Error(error.message || "Failed to send email");
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      return data;
    },
    onSuccess: (_, variables) => {
      // Invalidate email queries to refresh the thread
      queryClient.invalidateQueries({ queryKey: ["lead-emails", variables.leadId] });
      queryClient.invalidateQueries({ queryKey: ["lead-sent-emails", variables.leadId] });
      
      toast({
        title: "Email sent",
        description: "Your email has been sent successfully.",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to send email",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};
